import type { LaunchSequenceOut, ResolvedTouchOut } from "@services/sequence/sequenceService";
import { ctaFromBody } from "./touchCopy";
import type { Sequence, SequenceSettings, Touch } from "./emailSmsTypes";

/*
    The server speaks resolved touches; the components speak `Touch`. Adapting here rather
    than reshaping the components keeps the prototype's vocabulary in the UI and the API's
    vocabulary at the boundary, so neither has to bend to the other.

    `id` is the override id for an added step and the catalog touch id otherwise, prefixed so
    the two cannot collide — an added step and a catalog touch can share a numeric id.
*/
export const touchKey = (touch: ResolvedTouchOut): string =>
  touch.is_added ? `added-${touch.override_id}` : `touch-${touch.touch_id}`;

export const touchIdFromKey = (key: string): number | null => {
  const [, raw] = key.split("-");
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

export const isAddedKey = (key: string) => key.startsWith("added-");

export function toTouch(
  resolved: ResolvedTouchOut,
  codeById: Record<number, string>,
): Touch {
  // From the type registry, not this campaign's key dates: a touch keeps its anchor even
  // when the campaign has not dated that type, which is exactly when the warning fires.
  const anchor =
    resolved.key_date_type_id == null ? "" : (codeById[resolved.key_date_type_id] ?? "");
  return {
    id: touchKey(resolved),
    ch: resolved.channel,
    title: resolved.title,
    days: resolved.offset_days,
    dir: resolved.direction,
    anchor,
    hr: resolved.send_hour,
    grp: resolved.group_id == null ? "main" : String(resolved.group_id),
    on: resolved.is_on,
    pub: true,
    ver: 1,
    customized: resolved.is_customized,
    launchAdId: resolved.launch_ad_id ?? null,
    reviewStatus: resolved.launch_ad_status ?? null,
    reviewComment: resolved.review_comment ?? "",
    subject: resolved.subject,
    pre: resolved.preheader,
    body: resolved.html,
    text: resolved.text,
    /*
        The button label is not a column: the server stores the whole body, and the label
        lives inside its `class="button"` anchor. Read it back out, or the editor shows an
        empty Button label marked Required while the preview renders the real one.
    */
    cta: ctaFromBody(resolved.html).cta ?? "",
  };
}

export function toSequence(
  row: LaunchSequenceOut,
  codeById: Record<number, string>,
): Sequence {
  return {
    id: String(row.sequence_id),
    name: row.sequence_name,
    stage: row.stage ?? "",
    target: row.target_action,
    win: row.window,
    starter: "",
    published: row.sequence_status === "published",
    groups: row.groups.map((group) => ({
      id: String(group.id),
      name: group.name,
      note: group.note,
    })),
    emailCount:
      row.email_touch_count ??
      row.touches.filter((touch) => touch.channel === "email").length,
    smsCount:
      row.sms_touch_count ?? row.touches.filter((touch) => touch.channel === "sms").length,
    touches: row.touches.map((touch) => toTouch(touch, codeById)),
  };
}

export function toSettings(row: LaunchSequenceOut): SequenceSettings {
  return {
    on: row.is_on,
    emailCh: row.email_enabled,
    smsCh: row.sms_enabled,
    audMode: row.audience_mode,
    audPick: row.audience_id == null ? "" : String(row.audience_id),
  };
}
